import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, InputAdornment, Box, Autocomplete
} from '@mui/material';

const URL_REGEX = /^(https?:\/\/)[^\s$.?#].[^\s]*$/;
const PLATFORM_OPTION = '__PLATFORM_SUBDOMAIN__';
const PLATFORM_LABEL = 'Usar subdominio de la plataforma';
// Mirror the backend validators (ultimate_cloner/app.py) so bad input fails in
// the form, before steps 1-4 create orphaned domain/bucket/landing records.
const NAME_REGEX = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9 .,\-]{1,80}$/;
const PRICE_REGEX = /^\d{1,4}(\.\d{1,2})?$/; // dollars, ≤2 decimals, < 5 figures

const ClonePageModal = ({ open, onClose, onClone, isSubmitting, domains = [], domainsLoading = false }) => {
    const [form, setForm] = useState({
        url: '',
        productName: '',
        productPrice: '',
        domain: '',
        domainMode: 'NONE',
        pageName: ''
    });

    const [errors, setErrors] = useState({});

    const validate = () => {
        const newErrors = {};

        if (!form.url) {
            newErrors.url = 'La URL es obligatoria';
        } else if (!URL_REGEX.test(form.url)) {
            newErrors.url = 'Formato de URL inválido';
        }

        if (!form.productName.trim()) {
            newErrors.productName = 'El nombre del producto es obligatorio';
        } else if (!NAME_REGEX.test(form.productName.trim())) {
            newErrors.productName = 'Solo letras, números, espacios y . , - (máx. 80)';
        }

        if (!form.productPrice.trim()) {
            newErrors.productPrice = 'El precio es obligatorio';
        } else if (!PRICE_REGEX.test(form.productPrice.trim())) {
            newErrors.productPrice = 'Precio inválido (ej: 9.99)';
        }

        if (!form.pageName) {
            newErrors.pageName = 'El nombre de la página es obligatorio';
        } else if (/[^a-zA-Z0-9-_]/.test(form.pageName)) {
            newErrors.pageName = 'Solo caracteres alfanuméricos, guiones (-) y guiones bajos (_)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const resetAll = () => {
        setForm({ url: '', productName: '', productPrice: '', domain: '', domainMode: 'NONE', pageName: '' });
        setErrors({});
    };

    const handleSubmit = () => {
        if (!validate()) return;
        onClone({
            url: form.url,
            productName: form.productName.trim(),
            productPrice: form.productPrice.trim(),
            domain: form.domain,
            domainMode: form.domainMode,
            subdomain: form.domainMode === 'PLATFORM' ? form.pageName : '',
            pageName: form.pageName,
        });
        resetAll();
        onClose();
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetAll();
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    padding: '16px',
                    '& .MuiMenuItem-root': {
                        color: '#000000'
                    }
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.8rem', color: '#000000' }}>
                Clonar Página
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    <TextField
                        label="URL de la página a clonar"
                        fullWidth
                        value={form.url}
                        onChange={handleChange('url')}
                        error={!!errors.url}
                        helperText={errors.url}
                        disabled={isSubmitting}
                        placeholder="https://ejemplo.com/pagina"
                    />

                    <TextField
                        label="Nombre del producto / tema"
                        fullWidth
                        value={form.productName}
                        onChange={handleChange('productName')}
                        error={!!errors.productName}
                        helperText={errors.productName}
                        disabled={isSubmitting}
                        placeholder="El Pudin Perfecto"
                    />

                    <TextField
                        label="Precio (USD)"
                        fullWidth
                        value={form.productPrice}
                        onChange={handleChange('productPrice')}
                        error={!!errors.productPrice}
                        helperText={errors.productPrice}
                        disabled={isSubmitting}
                        placeholder="9.99"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                    />

                    <Autocomplete
                        freeSolo
                        fullWidth
                        slotProps={{
                            paper: {
                                sx: {
                                    color: '#000000',
                                    '& .MuiAutocomplete-option': {
                                        color: '#000000',
                                    },
                                },
                            },
                        }}
                        options={[PLATFORM_OPTION, ...domains.map((domain) => domain.Dominio || domain.domainName || domain.domain)]}
                        value={form.domainMode === 'PLATFORM' ? PLATFORM_OPTION : form.domain || null}
                        inputValue={form.domainMode === 'PLATFORM' ? PLATFORM_LABEL : form.domain}
                        getOptionLabel={(option) => option === PLATFORM_OPTION ? PLATFORM_LABEL : option}
                        onChange={(_, value) => {
                            if (value === PLATFORM_OPTION) {
                                setForm(prev => ({ ...prev, domain: '', domainMode: 'PLATFORM' }));
                            } else if (value) {
                                setForm(prev => ({ ...prev, domain: value, domainMode: 'CUSTOM' }));
                            } else {
                                setForm(prev => ({ ...prev, domain: '', domainMode: 'NONE' }));
                            }
                        }}
                        onInputChange={(_, value) => {
                            if (value === PLATFORM_LABEL) return;
                            setForm(prev => ({ ...prev, domain: value, domainMode: value ? 'CUSTOM' : 'NONE' }));
                            if (errors.domain) setErrors(prev => ({ ...prev, domain: null }));
                        }}
                        disabled={isSubmitting}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Dominio (opcional)"
                                error={!!errors.domain}
                                helperText={errors.domain || (domainsLoading
                                    ? 'Cargando tus dominios...'
                                    : domains.length
                                        ? 'Selecciona un dominio guardado o escribe otro.'
                                        : 'No tienes dominios guardados. Puedes escribir uno o configurarlo después.')}
                                placeholder="shop-a.com"
                            />
                        )}
                    />

                    <TextField
                        label="Nombre de la página"
                        fullWidth
                        value={form.pageName}
                        onChange={handleChange('pageName')}
                        error={!!errors.pageName}
                        helperText={errors.pageName}
                        disabled={isSubmitting}
                        placeholder="mi-pagina"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">/</InputAdornment>,
                        }}
                    />

                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    sx={{ textTransform: 'none', color: '#666' }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 4,
                        bgcolor: '#374151',
                        '&:hover': { bgcolor: '#1f2937' }
                    }}
                >
                    {isSubmitting ? 'Clonando...' : 'Clonar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClonePageModal;
